import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { useSiteConfig } from './contexts/SiteConfigContext';
import { normalizeCotwVisibility } from './lib/cotwVisibility';
import { getYoutubeRedirectUrl } from './lib/legacyMediaRedirect';
import ProtectedRoute from './components/ProtectedRoute';
import { PERMISSIONS } from './constants/permissions';

// Public Components
import LandingPage from './components/Public/LandingPage';
import NewsIndex from './components/Public/News/NewsIndex';
import NewsArticle from './components/Public/News/NewsArticle';
import Rules from './components/Public/Rules';
import Stream from './components/Public/Stream';
import Tournaments from './components/Public/Tournament';
import TournamentDetails from './components/Public/TournamentDetails';
import StageOverlay from './components/Public/stages/StageOverlay';
import CompOfTheWeek from './components/Public/CompOfTheWeek';
import UserSearch from './components/User/UserSearch';
import UserProfile from './components/User/UserProfile';
import UserSettings from './components/User/UserSettings';
import DiscordLink from './components/User/DiscordLink';
import MapGuessingContainer from './components/Public/MapGuessing/MapGuessingContainer';
import MapOfTheWeek from './components/Public/MapOfTheWeek';
import Contact from './components/Public/Contact';
import Imprint from './components/Public/Legal/Imprint';
import PrivacyPolicy from './components/Public/Legal/PrivacyPolicy';
import Terms from './components/Public/Legal/Terms';
import Servers from './components/Public/Servers';
import Staff from './components/Public/Staff';
import StaffManagement from './components/Admin/StaffManagement';
import Suspended from './components/Public/Suspended';
import SiteBanGuard from './components/SiteBanGuard';

// Auth Components
import UserSignup from './components/Auth/UserSignup';
import Login from './components/Auth/Login';

// Admin Components
import Dashboard from './components/Admin/Dashboard';
import TeamManagement from './components/Admin/TeamManagement';
import TournamentWinnerManagement from './components/Admin/TournamentWinnerManagement';
import SupporterTierManagement from './components/Admin/SupporterTierManagement';
import MapImageManagement from './components/Admin/MapImageManagement';
import MapSubmissionReview from './components/Admin/MapSubmissionReview';
import MediaManagement from './components/Admin/MediaManagement';
import ContentManagement from './components/Admin/ContentManagement';
import ServerManagement from './components/Admin/ServerManagement';
import MapOfTheWeekManagement from './components/Admin/MapOfTheWeekManagement';
import ServerAnnouncementsManagement from './components/Admin/ServerAnnouncementsManagement';
import UserManagement from './components/Admin/UserManagement';
import ModerationQueue from './components/Admin/ModerationQueue';
import NewsList from './components/Admin/News/NewsList';
import NewsEditor from './components/Admin/News/NewsEditor';

// Unified per-tournament admin (replaces a swarm of standalone screens)
import TournamentsListPage from './components/Admin/Tournament/TournamentsListPage';
import TournamentAdminPageV2 from './components/Admin/Tournament/TournamentAdminPageV2';

// Layouts
import Layout from './components/Layout/Layout';
import AdminLayout from './components/Admin/shell/AdminLayout';

import './App.css';

const requireAdmin = (element) => <ProtectedRoute requireAdmin={true}>{element}</ProtectedRoute>;
const requireSuperAdmin = (element) => (
  <ProtectedRoute requireSuperAdmin={true}>{element}</ProtectedRoute>
);
const requireTournamentAdmin = (element) => (
  <ProtectedRoute requireTournamentAdmin={true} tournamentIdFromPath={true}>
    {element}
  </ProtectedRoute>
);
const requirePermission = (permission, element) => (
  <ProtectedRoute requirePermission={permission}>{element}</ProtectedRoute>
);
const requireAnyPermission = (permissions, element) => (
  <ProtectedRoute requireAnyPermission={permissions}>{element}</ProtectedRoute>
);

const LegacyTournamentRedirect = () => {
  const { tournamentId } = useParams();
  return <Navigate to={`/admin/tournaments/${tournamentId}`} replace />;
};

const LegacyMediaRedirect = () => {
  const { config, loading } = useSiteConfig();
  const destination = getYoutubeRedirectUrl(config);

  useEffect(() => {
    if (!loading && destination) window.location.replace(destination);
  }, [destination, loading]);

  if (loading || destination) return null;
  return <Navigate to="/" replace />;
};

const CotwRoute = () => {
  const { config, loading } = useSiteConfig();
  if (loading) return null;
  return normalizeCotwVisibility(config) ? <CompOfTheWeek /> : <Navigate to="/" replace />;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <SiteBanGuard />
            <Routes>
              <Route path="/suspended" element={<Suspended />} />
              {/* Stream overlay: no site chrome, transparent background */}
              <Route path="/tournaments/:slug/overlay" element={<StageOverlay />} />
              {/* Public-facing routes */}
              <Route element={<Layout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/stream" element={<Stream />} />
                <Route path="/tournaments" element={<Tournaments />} />
                <Route path="/tournaments/:slug" element={<TournamentDetails />} />
                <Route path="/comp-of-the-week" element={<CotwRoute />} />
                <Route
                  path="/map-guessing"
                  element={
                    <ProtectedRoute>
                      <MapGuessingContainer />
                    </ProtectedRoute>
                  }
                />
                {/* Hidden page — intentionally NOT in SiteConfig navigation.links */}
                <Route
                  path="/map-of-the-week"
                  element={
                    <ProtectedRoute>
                      <MapOfTheWeek />
                    </ProtectedRoute>
                  }
                />
                {/* Renamed from the daily version; keep old links working. */}
                <Route
                  path="/map-of-the-day"
                  element={<Navigate to="/map-of-the-week" replace />}
                />
                <Route path="/media" element={<LegacyMediaRedirect />} />
                <Route path="/rules" element={<Rules />} />
                <Route path="/news" element={<NewsIndex />} />
                <Route path="/news/:slug" element={<NewsArticle />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/imprint" element={<Imprint />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/servers" element={<Servers />} />
                <Route path="/staff" element={<Staff />} />
                <Route path="/users" element={<UserSearch />} />
                <Route path="/user/:userId" element={<UserProfile />} />

                <Route path="/signup" element={<UserSignup />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<Login />} />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <UserSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/discord-link"
                  element={
                    <ProtectedRoute>
                      <DiscordLink />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Admin routes — new shell with sidebar + top bar + tournament context */}
              <Route element={<AdminLayout />}>
                <Route path="/admin/dashboard" element={requireAdmin(<Dashboard />)} />

                {/* Unified tournament admin */}
                <Route
                  path="/admin/tournaments"
                  element={requirePermission(
                    PERMISSIONS.TOURNAMENTS_MANAGE,
                    <TournamentsListPage />
                  )}
                />
                <Route
                  path="/admin/tournaments/:tournamentId"
                  element={requireTournamentAdmin(<TournamentAdminPageV2 />)}
                />
                <Route
                  path="/admin/tournament-winners"
                  element={requirePermission(
                    PERMISSIONS.TOURNAMENTS_MANAGE,
                    <TournamentWinnerManagement />
                  )}
                />
                <Route
                  path="/admin/supporters"
                  element={requirePermission(
                    PERMISSIONS.TOURNAMENTS_MANAGE,
                    <SupporterTierManagement />
                  )}
                />

                {/* Legacy redirects */}
                <Route path="/admin/players" element={<Navigate to="/admin/dashboard" replace />} />
                <Route
                  path="/admin/teams"
                  element={requirePermission(PERMISSIONS.TEAMS_MANAGE, <TeamManagement />)}
                />

                <Route
                  path="/admin/map-images"
                  element={requirePermission(PERMISSIONS.MAP_IMAGES_MANAGE, <MapImageManagement />)}
                />
                <Route
                  path="/admin/map-submissions"
                  element={requirePermission(
                    PERMISSIONS.MAP_SUBMISSIONS_REVIEW,
                    <MapSubmissionReview />
                  )}
                />
                <Route
                  path="/admin/media"
                  element={requirePermission(PERMISSIONS.MEDIA_MANAGE, <MediaManagement />)}
                />
                <Route
                  path="/admin/content"
                  element={requirePermission(PERMISSIONS.CONTENT_EDIT, <ContentManagement />)}
                />
                <Route
                  path="/admin/servers"
                  element={requirePermission(PERMISSIONS.SERVERS_MANAGE, <ServerManagement />)}
                />
                <Route
                  path="/admin/staff"
                  element={requirePermission(PERMISSIONS.STAFF_MANAGE, <StaffManagement />)}
                />
                <Route
                  path="/admin/announcements"
                  element={requirePermission(
                    PERMISSIONS.CONTENT_EDIT,
                    <ServerAnnouncementsManagement />
                  )}
                />
                <Route
                  path="/admin/map-of-the-week"
                  element={requirePermission(
                    PERMISSIONS.MAP_OF_THE_WEEK_MANAGE,
                    <MapOfTheWeekManagement />
                  )}
                />
                <Route
                  path="/admin/news"
                  element={requirePermission(PERMISSIONS.NEWS_MANAGE, <NewsList />)}
                />
                <Route
                  path="/admin/news/new"
                  element={requirePermission(PERMISSIONS.NEWS_MANAGE, <NewsEditor />)}
                />
                <Route
                  path="/admin/news/:id"
                  element={requirePermission(PERMISSIONS.NEWS_MANAGE, <NewsEditor />)}
                />

                <Route
                  path="/admin/moderation"
                  element={requireAnyPermission(
                    [PERMISSIONS.NEWS_MANAGE, PERMISSIONS.PROFILE_COMMENTS_MODERATE],
                    <ModerationQueue />
                  )}
                />

                {/* Unified user & permission management (replaces admin-management / user-management / content-admins) */}
                <Route path="/admin/users" element={requireSuperAdmin(<UserManagement />)} />
                <Route
                  path="/admin/admin-management"
                  element={<Navigate to="/admin/users" replace />}
                />
                <Route
                  path="/admin/user-management"
                  element={<Navigate to="/admin/users" replace />}
                />
                <Route
                  path="/admin/content-admins"
                  element={<Navigate to="/admin/users" replace />}
                />

                {/* Legacy per-tournament sub-routes → unified tournament admin */}
                <Route
                  path="/admin/tournaments/:tournamentId/*"
                  element={<LegacyTournamentRedirect />}
                />
                <Route
                  path="/admin/team-rounds/:tournamentId"
                  element={<LegacyTournamentRedirect />}
                />
                <Route
                  path="/admin/team-rounds"
                  element={<Navigate to="/admin/tournaments" replace />}
                />
              </Route>
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
